import { memo, useMemo, useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { alpha, useTheme } from "@mui/material/styles";
import {
  CODE_TO_USAGE_TYPE,
  QUESTIONNAIRE_TITLE_EXTENSION_URL,
  QUESTIONNAIRE_USAGE_TYPE_EXTENSION_URL,
} from "src-next/modules/questionnaire-builder/form-builder/utils/questionnaireQuestionnaireConstants";
import type { QuestionnaireUsageTypeDomain } from "../transformers/extractQuestionnaireResponsesFromSummary";
import {
  normalizeQuestionnaireLogicalIdForComparison,
  normalizeQuestionnaireReferenceToLogicalId,
} from "../transformers/questionnaireResponseExclusions";
import { patientQuestionnairesScrollbarSx } from "../utils/patientQuestionnairesScrollbarSx";
import { QuestionnaireProtocolLinkChips } from "@/components/common/QuestionnaireProtocolLinkChips";
import { useOrganizationProtocolQuestionnaireIndex } from "@/services/fhir/protocol/hooks/useOrganizationProtocolQuestionnaireIndex";
import { useFhirData } from "@/utils/useFhirData";

type UsageTabValue = "all" | QuestionnaireUsageTypeDomain;

type QaPair = {
  question: string;
  answer: string;
};

type PatientQuestionnaireRow = {
  id: string;
  questionnaireResponse: fhir5.QuestionnaireResponse;
  title: string;
  usageTypeDomain?: QuestionnaireUsageTypeDomain;
  usageTypeLabel: string;
  authoredDisplay: string;
  authoredAtIso?: string;
  responseIdentifierValue?: string;
  qaPairs: QaPair[];
};

export interface PatientQuestionnairesPanelProps {
  summaryData: fhir5.Bundle | undefined;
  excludeQuestionnaireLogicalIds?: readonly string[];
}

const USAGE_TABS: { value: UsageTabValue; label: string }[] = [
  { value: "all", label: "Todos" },
  { value: "triage", label: "Triagem" },
  { value: "preConsultation", label: "Pré-consulta" },
  { value: "postConsultation", label: "Pós-consulta" },
  { value: "attendance", label: "Acompanhamento" },
];

const AUTHORED_DATE_FORMATTER = new Intl.DateTimeFormat("pt-BR", {
  day: "numeric",
  month: "long",
  year: "numeric",
});

function formatAuthoredLabel(authoredIso: string | undefined | null): string {
  if (!authoredIso) return "—";
  const date = new Date(authoredIso);
  if (Number.isNaN(date.getTime())) return authoredIso;
  return AUTHORED_DATE_FORMATTER.format(date);
}

function questionnaireReferenceString(qr: fhir5.QuestionnaireResponse): string {
  const ref =
    typeof qr.questionnaire === "string"
      ? qr.questionnaire
      : (qr.questionnaire as { reference?: string } | undefined)?.reference;
  return String(ref ?? "").trim();
}

function usageTypeFromQuestionnaireResponse(qr: fhir5.QuestionnaireResponse): {
  usageTypeDomain: QuestionnaireUsageTypeDomain | undefined;
  usageTypeLabel: string;
} {
  const ext = qr.extension?.find(
    (extension: any) =>
      extension?.url === QUESTIONNAIRE_USAGE_TYPE_EXTENSION_URL,
  ) as any;
  if (!ext) return { usageTypeDomain: undefined, usageTypeLabel: "—" };

  const codeRaw =
    ext?.valueCode != null && String(ext.valueCode).trim() !== ""
      ? String(ext.valueCode).trim()
      : ext?.valueCoding?.code != null &&
          String(ext.valueCoding.code).trim() !== ""
        ? String(ext.valueCoding.code).trim()
        : null;

  if (codeRaw) {
    const domain = CODE_TO_USAGE_TYPE[codeRaw] as
      | QuestionnaireUsageTypeDomain
      | undefined;
    if (domain) {
      const label = USAGE_TABS.find((t) => t.value === domain)?.label ?? codeRaw;
      return { usageTypeDomain: domain, usageTypeLabel: label };
    }
    return { usageTypeDomain: undefined, usageTypeLabel: codeRaw };
  }

  const display = ext?.valueCoding?.display?.trim();
  if (display) return { usageTypeDomain: undefined, usageTypeLabel: display };
  const valueString = ext?.valueString?.trim();
  if (valueString) return { usageTypeDomain: undefined, usageTypeLabel: valueString };
  return { usageTypeDomain: undefined, usageTypeLabel: "—" };
}

function hasUsageTypeExtension(qr: fhir5.QuestionnaireResponse): boolean {
  return (qr.extension ?? []).some(
    (extension: any) => extension?.url === QUESTIONNAIRE_USAGE_TYPE_EXTENSION_URL,
  );
}

function extractQaPairs(qr: fhir5.QuestionnaireResponse): QaPair[] {
  const pairs: QaPair[] = [];
  const walkItems = (items: fhir5.QuestionnaireResponseItem[] | undefined) => {
    for (const item of items ?? []) {
      if (item.item?.length) {
        walkItems(item.item);
        continue;
      }
      const answers = (item.answer ?? [])
        .map((answer) =>
          answer.valueString != null
            ? String(answer.valueString)
            : answer.valueBoolean != null
              ? answer.valueBoolean
                ? "Sim"
                : "Não"
              : answer.valueCoding
                ? String(answer.valueCoding.display ?? answer.valueCoding.code ?? "")
                : "",
        )
        .filter((s) => s.length > 0);

      if (answers.length === 0 && !item.text) continue;
      pairs.push({
        question: item.text?.trim() || item.linkId?.trim() || "Pergunta",
        answer: answers.join(", ") || "—",
      });
    }
  };
  walkItems(qr.item);
  return pairs;
}

const PatientQuestionnairesPanel = memo(
  ({ summaryData, excludeQuestionnaireLogicalIds }: PatientQuestionnairesPanelProps) => {
    const theme = useTheme();
    const scrollbarSx = useMemo(() => patientQuestionnairesScrollbarSx(theme), [theme]);
    const [usageTab, setUsageTab] = useState<UsageTabValue>("all");
    const [selected, setSelected] = useState<PatientQuestionnaireRow | null>(null);
    const { status: protocolIndexStatus, getProtocolNamesForQuestionnaire } =
      useOrganizationProtocolQuestionnaireIndex({ enabled: true });

    const excludeSet = useMemo(
      () =>
        new Set(
          (excludeQuestionnaireLogicalIds ?? [])
            .map((id) => normalizeQuestionnaireLogicalIdForComparison(id) ?? "")
            .filter(Boolean),
        ),
      [excludeQuestionnaireLogicalIds],
    );

    const rows = useMemo((): PatientQuestionnaireRow[] => {
      if (!summaryData) return [];

      const questionnaireSubBundles = useFhirData.extractPatientSummaryResource(
        summaryData,
        "QuestionnaireResponse",
      ) as Array<{ entry?: any[] }> | undefined;

      const entries = (questionnaireSubBundles ?? [])
        .flatMap((bundle) => (Array.isArray(bundle?.entry) ? bundle.entry : []))
        .filter(Boolean);

      return entries
        .map((entry: any) => entry?.resource)
        .filter(
          (resource: any): resource is fhir5.QuestionnaireResponse =>
            resource && resource.resourceType === "QuestionnaireResponse",
        )
        .filter(hasUsageTypeExtension)
        .filter((qr) => {
          if (!excludeSet.size) return true;
          const ref = questionnaireReferenceString(qr);
          const logicalIdRaw = ref.includes("/") ? ref.split("/")[1] : ref;
          const normalized =
            normalizeQuestionnaireLogicalIdForComparison(logicalIdRaw);
          return !normalized || !excludeSet.has(normalized);
        })
        .map((qr) => {
          const usage = usageTypeFromQuestionnaireResponse(qr);
          const titleFromExt = qr.extension?.find(
            (ext: any) => ext?.url === QUESTIONNAIRE_TITLE_EXTENSION_URL,
          ) as any;
          const title =
            String(titleFromExt?.valueString ?? "").trim() ||
            questionnaireReferenceString(qr) ||
            "Questionário";
          const authored = String(qr.authored ?? "").trim();
          return {
            id: qr.id ?? `${title}-${authored}`,
            questionnaireResponse: qr,
            title,
            usageTypeDomain: usage.usageTypeDomain,
            usageTypeLabel: usage.usageTypeLabel,
            authoredDisplay: formatAuthoredLabel(authored),
            authoredAtIso: authored || undefined,
            responseIdentifierValue: qr.identifier?.[0]?.value?.trim(),
            qaPairs: extractQaPairs(qr),
          };
        })
        .sort((a, b) => new Date(b.authoredAtIso ?? 0).getTime() - new Date(a.authoredAtIso ?? 0).getTime());
    }, [summaryData, excludeSet]);

    const filteredRows = useMemo(() => {
      if (usageTab === "all") return rows;
      return rows.filter((row) => row.usageTypeDomain === usageTab);
    }, [rows, usageTab]);

    const handleCopyAnswer = async (answer: string) => {
      const textToCopy = String(answer ?? "").trim();
      if (!textToCopy) return;
      await navigator.clipboard.writeText(textToCopy);
    };

    return (
      <Stack sx={{ height: "100%", minHeight: 0 }}>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflowY: "auto",
            ...scrollbarSx,
          }}
        >
          {!summaryData && (
            <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
              O resumo do paciente ainda não está disponível neste atendimento.
            </Alert>
          )}

          {summaryData && rows.length === 0 && (
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="body2" color="text.secondary">
                  Nenhuma resposta de questionário encontrada neste resumo.
                </Typography>
              </CardContent>
            </Card>
          )}

          {summaryData && rows.length > 0 && !selected && (
            <Card
              variant="outlined"
              sx={{ borderRadius: 2, display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}
            >
              <CardContent sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
                <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
                  <Tabs
                    value={usageTab}
                    onChange={(_e, next: UsageTabValue) => setUsageTab(next)}
                    variant="scrollable"
                    scrollButtons="auto"
                  >
                    {USAGE_TABS.map((tab) => (
                      <Tab
                        key={tab.value}
                        label={tab.label}
                        value={tab.value}
                        sx={{ textTransform: "none" }}
                      />
                    ))}
                  </Tabs>
                  <Divider />
                  <List
                    dense
                    disablePadding
                    sx={{ flex: 1, minHeight: 0, overflowY: "auto", ...scrollbarSx }}
                  >
                    {filteredRows.map((row) => (
                      <ListItem key={row.id} disablePadding sx={{ mb: 0.5 }}>
                        <ListItemButton onClick={() => setSelected(row)} sx={{ borderRadius: 2 }}>
                          <ListItemText
                            primary={
                              <Stack direction="row" spacing={1} alignItems="flex-start">
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>
                                  {row.title}
                                </Typography>
                                <Chip label={row.usageTypeLabel} size="small" variant="outlined" />
                              </Stack>
                            }
                            secondary={
                              <Typography variant="body2" sx={{ mt: 0.5 }}>
                                {row.authoredDisplay}
                              </Typography>
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Stack>
              </CardContent>
            </Card>
          )}

          {selected && (
            <Stack spacing={2}>
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="flex-start">
                      <IconButton
                        size="small"
                        aria-label="Voltar para lista"
                        onClick={() => setSelected(null)}
                      >
                        <ArrowBackIcon fontSize="small" />
                      </IconButton>
                      <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" spacing={1} alignItems="flex-start">
                          <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
                            {selected.title}
                          </Typography>
                          <Chip
                            label={selected.usageTypeLabel}
                            size="small"
                            variant="outlined"
                          />
                        </Stack>
                        <Typography variant="body1" fontWeight={600}>
                          {selected.authoredDisplay}
                        </Typography>
                      </Stack>
                    </Stack>
                    {selected.responseIdentifierValue ? (
                      <Typography variant="caption" color="text.secondary">
                        Identificador: {selected.responseIdentifierValue}
                      </Typography>
                    ) : null}
                    {selected.usageTypeDomain === "postConsultation" ? (
                      <QuestionnaireProtocolLinkChips
                        protocolNames={getProtocolNamesForQuestionnaire(
                          questionnaireReferenceString(selected.questionnaireResponse),
                        )}
                        status={protocolIndexStatus}
                      />
                    ) : null}
                  </Stack>
                </CardContent>
              </Card>

              <Stack spacing={1.5}>
                {selected.qaPairs.map((pair, idx) => (
                  <Card key={`${selected.id}-q-${idx}`} variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent>
                      <Stack direction="row" spacing={1.25} alignItems="flex-start">
                        <HelpOutlineIcon sx={{ fontSize: 20, color: "primary.main", mt: 0.125 }} />
                        <Box sx={{ flex: 1 }}>
                          <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            spacing={1}
                          >
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {pair.question}
                            </Typography>
                            <Tooltip title="Copiar resposta">
                              <IconButton
                                size="small"
                                aria-label="Copiar resposta"
                                onClick={() => handleCopyAnswer(pair.answer)}
                              >
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                          <Box
                            sx={{
                              mt: 1.5,
                              p: 1.5,
                              borderRadius: 1,
                              border: 1,
                              borderColor: "divider",
                              bgcolor:
                                theme.palette.mode === "dark"
                                  ? alpha(theme.palette.common.white, 0.06)
                                  : theme.palette.grey[50],
                            }}
                          >
                            <Stack direction="row" spacing={1} alignItems="flex-start">
                              <ChatBubbleOutlineIcon sx={{ fontSize: 18, color: "text.secondary", mt: 0.125 }} />
                              <Typography variant="body2" color="text.secondary">
                                {pair.answer}
                              </Typography>
                            </Stack>
                          </Box>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </Stack>
          )}
        </Box>
      </Stack>
    );
  },
);

PatientQuestionnairesPanel.displayName = "PatientQuestionnairesPanel";

export { PatientQuestionnairesPanel };
